import { ref, watch, onUnmounted, type Ref } from 'vue'

import { logService, LOG_CATEGORIES } from '@/services/logService'
import { loadTourGeometries, loadWorkspaceGeometries } from '@/services/trackGeometryProvider'

import type { Map as MapLibreMap } from 'maplibre-gl'

export type Resolution = '500m' | '50m' | '5m'

// Upgrade thresholds: zoom level at/above which we switch to the higher
// resolution while zooming IN. Lowered vs. the old values (10 / 13) so the
// crisper geometry appears one zoom level earlier.
const UPGRADE_THRESHOLD_50M = 9
const UPGRADE_THRESHOLD_5M = 12

// Downgrade thresholds: zoom level BELOW which we drop back to the lower
// resolution while zooming OUT. Kept distinctly lower than the upgrade
// thresholds so the resolution doesn't flip back and forth on small zoom
// jitter around a single boundary (hysteresis).
const DOWNGRADE_THRESHOLD_50M = 7
const DOWNGRADE_THRESHOLD_5M = 10

const DEBOUNCE_MS = 25

/**
 * Pick a resolution for the given zoom with hysteresis: the boundary at which
 * we upgrade (zooming in) is higher than the boundary at which we downgrade
 * (zooming out), so the resolution stays stable across small zoom changes near
 * a threshold. `current` is the resolution currently applied.
 */
export function getResolutionWithHysteresis(zoom: number, current: Resolution): Resolution {
  switch (current) {
    case '500m':
      // Only upgrades possible; a large jump can skip straight to 5m.
      if (zoom >= UPGRADE_THRESHOLD_5M) return '5m'
      if (zoom >= UPGRADE_THRESHOLD_50M) return '50m'
      return '500m'
    case '50m':
      if (zoom >= UPGRADE_THRESHOLD_5M) return '5m'
      if (zoom < DOWNGRADE_THRESHOLD_50M) return '500m'
      return '50m'
    case '5m':
      // Only downgrades possible; a large jump out can skip straight to 500m.
      if (zoom < DOWNGRADE_THRESHOLD_50M) return '500m'
      if (zoom < DOWNGRADE_THRESHOLD_5M) return '50m'
      return '5m'
  }
}

/**
 * Resolution for an initial pick with no prior state (equivalent to starting
 * from the lowest resolution, i.e. only the upgrade thresholds apply).
 */
export function getResolutionForZoom(zoom: number): Resolution {
  return getResolutionWithHysteresis(zoom, '500m')
}

type CacheEntry = {
  '50m'?: [number, number][][]
  '5m'?: [number, number][][]
}

export function useTrackResolution(mapInstance: Ref<MapLibreMap | null>, mapLoaded: Ref<boolean>) {
  const targetResolution = ref<Resolution>('500m')

  const wsCache = new Map<string, CacheEntry>()
  const tourCache = new Map<string, CacheEntry>()

  let debounceTimer: ReturnType<typeof setTimeout> | null = null
  let abortController: AbortController | null = null

  const isMapTransitioning = (map: MapLibreMap) => {
    const maybeMap = map as Partial<Pick<MapLibreMap, 'isMoving' | 'isZooming' | 'isEasing'>>
    return !!(maybeMap.isMoving?.() || maybeMap.isZooming?.() || maybeMap.isEasing?.())
  }

  const onZoomEnd = () => {
    const map = mapInstance.value
    if (!map) return
    const zoom = map.getZoom()
    const newRes = getResolutionWithHysteresis(zoom, targetResolution.value)
    if (newRes !== targetResolution.value) {
      targetResolution.value = newRes
    }
  }

  // Setup zoom listener when map is ready
  watch(
    [mapInstance, mapLoaded],
    ([map, loaded]) => {
      if (map && loaded) {
        map.on('zoomend', onZoomEnd)
        onZoomEnd()
      }
    },
    { immediate: true },
  )

  onUnmounted(() => {
    if (mapInstance.value) {
      mapInstance.value.off('zoomend', onZoomEnd)
    }
    if (debounceTimer) clearTimeout(debounceTimer)
    if (abortController) abortController.abort()
  })

  /**
   * Get IDs of entities currently visible in the map viewport.
   */
  function getVisibleEntityIds(layerPrefix: string, propertyKey: string): string[] {
    const map = mapInstance.value
    if (!map) return []

    const layers =
      map
        .getStyle()
        .layers?.filter((l) => l.id.startsWith(layerPrefix))
        .map((l) => l.id) ?? []

    if (layers.length === 0) return []

    const features = map.queryRenderedFeatures(undefined, { layers })
    const ids = new Set<string>()
    for (const f of features) {
      const id = f.properties?.[propertyKey]
      if (id) ids.add(id)
    }
    return Array.from(ids)
  }

  /**
   * Fetch and cache workspace geometries at the given resolution.
   */
  async function fetchWorkspaceGeometries(
    workspaceIds: string[],
    resolution: '50m' | '5m',
    signal?: AbortSignal,
  ): Promise<Map<string, [number, number][][]>> {
    const needed = workspaceIds.filter((id) => !wsCache.get(id)?.[resolution])

    if (needed.length > 0) {
      try {
        const results = await loadWorkspaceGeometries(needed, resolution, signal)
        for (const [clientId, parsed] of results) {
          const entry = wsCache.get(clientId) ?? {}
          entry[resolution] = parsed
          wsCache.set(clientId, entry)
        }
      } catch (error) {
        if ((error as Error).name === 'CanceledError') return new Map()
        logService.warn(LOG_CATEGORIES.MAP, 'Failed to fetch workspace geometries', {
          resolution,
          error,
        })
      }
    }

    const result = new Map<string, [number, number][][]>()
    for (const id of workspaceIds) {
      const cached = wsCache.get(id)?.[resolution]
      if (cached) result.set(id, cached)
    }
    return result
  }

  /**
   * Fetch and cache tour geometries at the given resolution.
   */
  async function fetchTourGeometries(
    tourIds: string[],
    resolution: '50m' | '5m',
    signal?: AbortSignal,
  ): Promise<Map<string, [number, number][][]>> {
    const needed = tourIds.filter((id) => !tourCache.get(id)?.[resolution])

    if (needed.length > 0) {
      try {
        const results = await loadTourGeometries(needed, resolution, signal)
        for (const [clientId, parsed] of results) {
          const entry = tourCache.get(clientId) ?? {}
          entry[resolution] = parsed
          tourCache.set(clientId, entry)
        }
      } catch (error) {
        if ((error as Error).name === 'CanceledError') return new Map()
        logService.warn(LOG_CATEGORIES.MAP, 'Failed to fetch tour geometries', {
          resolution,
          error,
        })
      }
    }

    const result = new Map<string, [number, number][][]>()
    for (const id of tourIds) {
      const cached = tourCache.get(id)?.[resolution]
      if (cached) result.set(id, cached)
    }
    return result
  }

  /**
   * Schedule a debounced resolution update for visible entities.
   * Call this from layer components when targetResolution changes.
   * Returns a cleanup function to cancel pending requests.
   */
  function scheduleUpdate(callback: (signal: AbortSignal) => Promise<void>): void {
    if (debounceTimer) clearTimeout(debounceTimer)
    if (abortController) abortController.abort()

    // Defer until the user actually pauses: if the map is still moving/zooming
    // when the timer fires, reschedule. This avoids a full source swap mid-gesture.
    const fire = () => {
      const map = mapInstance.value
      if (map && isMapTransitioning(map)) {
        debounceTimer = setTimeout(fire, DEBOUNCE_MS)
        return
      }
      abortController = new AbortController()
      callback(abortController.signal).catch(() => {
        // Abort errors are expected when a newer update supersedes this one
      })
    }

    debounceTimer = setTimeout(fire, DEBOUNCE_MS)
  }

  function clearCache() {
    wsCache.clear()
    tourCache.clear()
  }

  return {
    targetResolution,
    getVisibleEntityIds,
    fetchWorkspaceGeometries,
    fetchTourGeometries,
    scheduleUpdate,
    clearCache,
  }
}
